import { locationRowMatchesJurisdictionKeys } from "./jurisdictionCountyMatch";
import { parseProfileJurisdiction } from "./jurisdictionKeys";
import type { CountyStatePair } from "./mapboxCountyStatePairs";

export type PipelineTapGateResolution =
  | { allowed: true; reason: "unrestricted" | "jurisdiction_match" }
  | { allowed: false; reason: "no_county_state" | "no_jurisdiction_match" | "no_profile_keys" };

/**
 * Whether pipeline vector details may be shown for a tap at `lng/lat`, using the same profile +
 * jurisdiction key sources as the rest of web (`parseProfileJurisdiction` + optional stored keys).
 */
export function resolvePipelineMapTapAllowed(params: {
  registrationProfileData: unknown | null;
  jurisdictionCountyStateKeys: ReadonlySet<string> | null;
  geoPairs: readonly CountyStatePair[];
}): PipelineTapGateResolution {
  const { registrationProfileData, jurisdictionCountyStateKeys, geoPairs } = params;

  if (!geoPairs.length) return { allowed: false, reason: "no_county_state" };

  const profile = parseProfileJurisdiction(registrationProfileData ?? null);

  if (profile.kind === "unrestricted") {
    return { allowed: true, reason: "unrestricted" };
  }

  const keysFromStorage =
    jurisdictionCountyStateKeys && jurisdictionCountyStateKeys.size > 0
      ? jurisdictionCountyStateKeys
      : null;
  const keysFromProfile = profile.kind === "restricted" && profile.keys.size > 0 ? profile.keys : null;
  const effectiveKeys = keysFromStorage ?? keysFromProfile;

  if (!effectiveKeys || effectiveKeys.size === 0) {
    return { allowed: false, reason: "no_profile_keys" };
  }

  const hit = geoPairs.some((pair) =>
    locationRowMatchesJurisdictionKeys(pair.county, pair.state, effectiveKeys),
  );

  return hit ? { allowed: true, reason: "jurisdiction_match" } : { allowed: false, reason: "no_jurisdiction_match" };
}
